import HuddleService from "@/src/api/HuddleService.js";
import { livekitManager } from "@/src/services/chats/livekitManager.js";
import { stopCallingSound, playCallEndSound } from "@/src/utils/audioNotification.js";

export default {
  namespaced: true,

  state: {
    activeHuddle: null,
    participants: [],
    activeSpeakerIds: [],
    isMuted: false,
    videoEnabled: false,
    screenSharing: false,
    isJoining: false,
    isLeaving: false,
    connectionState: "disconnected",
    incomingInvite: null,
    panelMinimized: false,
    memberGroupIds: [],
    error: null,
  },

  mutations: {
    SET_ACTIVE_HUDDLE(state, huddle) {
      state.activeHuddle = huddle;
    },

    SET_PARTICIPANTS(state, participants) {
      state.participants = participants;
    },

    ADD_PARTICIPANT(state, participant) {
      const exists = state.participants.find(
        (p) => String(p.user_id) === String(participant.user_id)
      );
      if (!exists) {
        state.participants = [...state.participants, participant];
      }
    },

    REMOVE_PARTICIPANT(state, userId) {
      state.participants = state.participants.filter(
        (p) => String(p.user_id) !== String(userId)
      );
    },

    UPDATE_PARTICIPANT_MUTE(state, { userId, muted }) {
      const index = state.participants.findIndex(
        (p) => String(p.user_id) === String(userId)
      );
      if (index !== -1) {
        const updated = [...state.participants];
        updated[index] = { ...updated[index], muted };
        state.participants = updated;
      }
    },

    SYNC_REMOTE_PARTICIPANTS(state, { remoteParticipants, localUserId }) {
      const updated = [];

      for (const p of state.participants) {
        const isLocalUser = String(p.user_id) === String(localUserId);
        const remote = remoteParticipants.find(
          (r) => String(r.user_id) === String(p.user_id)
        );

        if (remote) {
          updated.push({
            ...p,
            muted: remote.muted,
            video_enabled: remote.video_enabled,
            screen_sharing: remote.screen_sharing,
            speaking: remote.speaking,
            identity: remote.identity,
          });
        } else if (isLocalUser) {
          updated.push(p);
        }
        // else: remote user no longer in LiveKit room — they left, drop them
      }

      const existingIds = new Set(updated.map((p) => String(p.user_id)));
      for (const remote of remoteParticipants) {
        if (!existingIds.has(String(remote.user_id))) {
          updated.push({
            user_id: remote.user_id,
            identity: remote.identity,
            muted: remote.muted,
            video_enabled: remote.video_enabled,
            screen_sharing: remote.screen_sharing,
            speaking: remote.speaking,
            joined_at: new Date().toISOString(),
          });
        }
      }

      state.participants = updated;
    },

    SET_ACTIVE_SPEAKERS(state, speakerIds) {
      state.activeSpeakerIds = speakerIds;
    },

    SET_MUTED(state, muted) {
      state.isMuted = muted;
    },

    SET_VIDEO_ENABLED(state, enabled) {
      state.videoEnabled = enabled;
    },

    SET_SCREEN_SHARING(state, sharing) {
      state.screenSharing = sharing;
    },

    SET_IS_JOINING(state, val) {
      state.isJoining = val;
    },

    SET_IS_LEAVING(state, val) {
      state.isLeaving = val;
    },

    SET_CONNECTION_STATE(state, connectionState) {
      state.connectionState = connectionState;
    },

    SET_INCOMING_INVITE(state, invite) {
      state.incomingInvite = invite;
    },

    SET_PANEL_MINIMIZED(state, val) {
      state.panelMinimized = val;
    },

    SET_MEMBER_GROUP_IDS(state, groupIds) {
      state.memberGroupIds = groupIds;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_HUDDLE(state) {
      state.activeHuddle = null;
      state.participants = [];
      state.activeSpeakerIds = [];
      state.isMuted = false;
      state.videoEnabled = false;
      state.screenSharing = false;
      state.isJoining = false;
      state.isLeaving = false;
      state.connectionState = "disconnected";
      state.panelMinimized = false;
      state.error = null;
    },
  },

  actions: {
    async startHuddle({ commit, state }, { groupId, memberIds }) {
      try {
        if (state.connectionState === "connected") {
          commit("SET_ERROR", "You are already in a huddle. Leave the current huddle first.");
          return null;
        }

        commit("SET_ERROR", null);

        // Check if a huddle already exists for this group before setting joining state
        const existing = await HuddleService.getActiveHuddle(groupId);
        if (existing?.huddle) {
          commit("SET_ACTIVE_HUDDLE", existing.huddle);
          commit("SET_PARTICIPANTS", existing.huddle.participants || []);
          commit("SET_ERROR", "A huddle is already active in this group. Click again to join.");
          return null;
        }

        commit("SET_IS_JOINING", true);
        commit("SET_CONNECTION_STATE", "connecting");

        const response = await HuddleService.startHuddle(groupId, memberIds);
        console.log("[Huddle] startHuddle response:", response);

        const huddleData = response?.huddle || response;
        if (huddleData?.session_id) {
          commit("SET_ACTIVE_HUDDLE", huddleData);
          commit("SET_PARTICIPANTS", huddleData.participants || []);

          // Connect to LiveKit
          const wsUrl = huddleData.livekit_ws_url;
          const token = huddleData.livekit_token;
          if (wsUrl && token) {
            await livekitManager.connect(wsUrl, token);
            commit("SET_CONNECTION_STATE", "connected");
            commit("SET_MUTED", false);
            commit("SET_VIDEO_ENABLED", false);
            commit("SET_SCREEN_SHARING", false);
          } else {
            console.error("[Huddle] Missing LiveKit credentials in startHuddle response:", { wsUrl: !!wsUrl, token: !!token });
            commit("SET_CONNECTION_STATE", "disconnected");
            commit("SET_ERROR", "Failed to connect to huddle — missing connection details");
          }
        } else {
          console.warn("[Huddle] Unexpected response structure:", response);
          commit("SET_CONNECTION_STATE", "disconnected");
        }

        return response;
      } catch (error) {
        await livekitManager.disconnect();
        console.error("[Huddle] startHuddle error:", error);
        const status = error?.response?.status;
        const errMsg = error?.response?.data?.error || "";
        if (error.message === "PERMISSION_DENIED:microphone") {
          commit("SET_ERROR", "Microphone permission denied. Please allow microphone access in your browser settings and try again.");
        } else if (error.message === "DEVICE_NOT_FOUND:microphone") {
          commit("SET_ERROR", "No microphone found. Please connect a microphone and try again.");
        } else if (error.message === "AUDIO_FAILED") {
          commit("SET_ERROR", "Failed to access microphone. Please check your audio settings.");
        } else if (status === 409 || (status === 500 && errMsg.includes("already active"))) {
          // Fetch the existing huddle so the user can join on next click
          try {
            const existing = await HuddleService.getActiveHuddle(groupId);
            if (existing?.huddle) {
              commit("SET_ACTIVE_HUDDLE", existing.huddle);
              commit("SET_PARTICIPANTS", existing.huddle.participants || []);
            }
          } catch { /* best-effort */ }
          commit("SET_ERROR", "A huddle is already active in this group. Click again to join.");
        } else {
          commit("SET_ERROR", "Failed to start huddle");
        }
        commit("SET_CONNECTION_STATE", "disconnected");
        throw error;
      } finally {
        commit("SET_IS_JOINING", false);
      }
    },

    async joinHuddle({ commit, state }, { sessionId, groupId }) {
      try {
        stopCallingSound();

        if (state.connectionState === "connected") {
          commit("SET_ERROR", "You are already in a huddle. Leave the current huddle first.");
          return null;
        }

        commit("SET_IS_JOINING", true);
        commit("SET_CONNECTION_STATE", "connecting");
        commit("SET_ERROR", null);

        const response = await HuddleService.joinHuddle(sessionId);
        console.log("[Huddle] joinHuddle response:", response);

        const joinData = response?.join_info || response;
        if (joinData?.livekit_token) {
          const wsUrl = joinData.livekit_ws_url;
          const token = joinData.livekit_token;

          // Always set activeHuddle to the session being joined — the join response is authoritative
          commit("SET_ACTIVE_HUDDLE", {
            session_id: joinData.session_id || sessionId,
            group_id: groupId || joinData.group_id,
            livekit_token: token,
            livekit_ws_url: wsUrl,
            livekit_room_name: joinData.room_name,
          });

          // Connect to LiveKit
          if (wsUrl && token) {
            await livekitManager.connect(wsUrl, token);

            // Fetch participants after joining
            const sid = joinData.session_id || sessionId;
            try {
              const partResponse = await HuddleService.fetchParticipants(sid);
              if (partResponse?.participants) {
                commit("SET_PARTICIPANTS", partResponse.participants);
              }
            } catch (e) {
              console.warn("[Huddle] Failed to fetch participants after join:", e);
            }

            commit("SET_CONNECTION_STATE", "connected");
            commit("SET_MUTED", false);
            commit("SET_VIDEO_ENABLED", false);
            commit("SET_SCREEN_SHARING", false);
          } else {
            console.error("[Huddle] Missing LiveKit credentials in joinHuddle response:", { wsUrl: !!wsUrl, token: !!token });
            commit("SET_CONNECTION_STATE", "disconnected");
            commit("SET_ERROR", "Failed to connect to huddle — missing connection details");
          }
        } else {
          console.warn("[Huddle] Unexpected join response:", response);
          commit("SET_CONNECTION_STATE", "disconnected");
        }

        return response;
      } catch (error) {
        await livekitManager.disconnect();
        console.error("[Huddle] joinHuddle error:", error);
        if (error.message === "PERMISSION_DENIED:microphone") {
          commit("SET_ERROR", "Microphone permission denied. Please allow microphone access in your browser settings and try again.");
        } else if (error.message === "DEVICE_NOT_FOUND:microphone") {
          commit("SET_ERROR", "No microphone found. Please connect a microphone and try again.");
        } else if (error.message === "AUDIO_FAILED") {
          commit("SET_ERROR", "Failed to access microphone. Please check your audio settings.");
        } else {
          commit("SET_ERROR", "Failed to join huddle");
        }
        commit("SET_CONNECTION_STATE", "disconnected");
        throw error;
      } finally {
        commit("SET_IS_JOINING", false);
      }
    },

    async leaveHuddle({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;

        commit("SET_IS_LEAVING", true);

        // Best-effort API call — don't block disconnect if it fails
        try {
          await HuddleService.leaveHuddle(state.activeHuddle.session_id);
        } catch (apiError) {
          console.error("[Huddle] leaveHuddle API failed (will disconnect anyway):", apiError);
        }

        // Mark disconnected BEFORE livekitManager.disconnect() to prevent
        // RoomEvent.Disconnected from triggering handleConnectionLost
        commit("SET_CONNECTION_STATE", "disconnected");
        await livekitManager.disconnect();
        playCallEndSound();
        commit("CLEAR_HUDDLE");
      } catch (error) {
        // Even if something unexpected fails, force cleanup
        commit("SET_CONNECTION_STATE", "disconnected");
        await livekitManager.disconnect();
        commit("CLEAR_HUDDLE");
        console.error("[Huddle] leaveHuddle error:", error);
      } finally {
        commit("SET_IS_LEAVING", false);
      }
    },

    async endHuddle({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;

        commit("SET_IS_LEAVING", true);
        await HuddleService.endHuddle(state.activeHuddle.session_id);
        commit("SET_CONNECTION_STATE", "disconnected");
        await livekitManager.disconnect();
        commit("CLEAR_HUDDLE");
      } catch (error) {
        commit("SET_CONNECTION_STATE", "disconnected");
        await livekitManager.disconnect();
        commit("CLEAR_HUDDLE");
        console.error("[Huddle] endHuddle error:", error);
      } finally {
        commit("SET_IS_LEAVING", false);
      }
    },

    async toggleMute({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;

        const newMuted = !state.isMuted;
        // Toggle actual media first, then notify server
        await livekitManager.toggleMute(newMuted);
        commit("SET_MUTED", newMuted);
        await HuddleService.toggleMute(state.activeHuddle.session_id, newMuted);
      } catch (error) {
        commit("SET_ERROR", "Failed to toggle mute");
        throw error;
      }
    },

    async toggleVideo({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;

        if (state.videoEnabled) {
          commit("SET_VIDEO_ENABLED", false);
          await livekitManager.disableCamera();
        } else {
          const track = await livekitManager.enableCamera();
          if (!track) {
            commit("SET_ERROR", "Camera access is required to enable video");
            return;
          }
          commit("SET_VIDEO_ENABLED", true);
        }
      } catch (error) {
        if (error.message === "PERMISSION_DENIED:camera") {
          commit("SET_ERROR", "Camera permission denied. Please allow camera access in your browser settings and try again.");
        } else if (error.message === "DEVICE_NOT_FOUND:camera") {
          commit("SET_ERROR", "No camera found. Please connect a camera and try again.");
        } else {
          commit("SET_ERROR", "Failed to toggle video");
        }
        throw error;
      }
    },

    async toggleScreenShare({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;

        if (state.screenSharing) {
          await livekitManager.disableScreenShare();
          commit("SET_SCREEN_SHARING", false);
          const cleared = state.participants.map((p) => ({ ...p, screen_sharing: false }));
          commit("SET_PARTICIPANTS", cleared);
        } else {
          const track = await livekitManager.enableScreenShare();
          if (!track) {
            return;
          }
          commit("SET_SCREEN_SHARING", true);
        }
      } catch (error) {
        if (error.message === "PERMISSION_DENIED:screen") {
          commit("SET_ERROR", "Screen sharing permission denied. Please allow screen sharing when prompted by your browser.");
        } else {
          commit("SET_ERROR", "Failed to toggle screen share");
        }
        throw error;
      }
    },

    async handleConnectionLost({ commit, state }) {
      // Network dropped during active huddle — clean up local state
      // Don't call API (network is likely down), don't call livekitManager.disconnect()
      // (the Disconnected event already means the room is gone)
      try {
        if (state.activeHuddle?.session_id) {
          await HuddleService.leaveHuddle(state.activeHuddle.session_id).catch(() => {});
        }
      } catch { /* best-effort */ }
      playCallEndSound();
      commit("CLEAR_HUDDLE");
    },

    browserEndedScreenShare({ commit, state }) {
      if (state.screenSharing) {
        commit("SET_SCREEN_SHARING", false);
        const cleared = state.participants.map((p) => ({ ...p, screen_sharing: false }));
        commit("SET_PARTICIPANTS", cleared);
      }
    },

    async fetchActiveHuddle({ commit, state }, { groupId }) {
      try {
        // Don't overwrite state if user is currently connected to a huddle
        if (state.connectionState === "connected") return;

        const response = await HuddleService.getActiveHuddle(groupId);
        if (response?.huddle) {
          commit("SET_ACTIVE_HUDDLE", response.huddle);
          commit("SET_PARTICIPANTS", response.huddle.participants || []);
        } else {
          commit("SET_ACTIVE_HUDDLE", null);
          commit("SET_PARTICIPANTS", []);
        }
      } catch (error) {
        if (state.connectionState !== "connected") {
          commit("SET_ACTIVE_HUDDLE", null);
          commit("SET_PARTICIPANTS", []);
        }
      }
    },

    async fetchParticipants({ commit, state }) {
      try {
        if (!state.activeHuddle?.session_id) return;
        const response = await HuddleService.fetchParticipants(
          state.activeHuddle.session_id
        );
        if (response?.participants) {
          commit("SET_PARTICIPANTS", response.participants);
        }
      } catch (error) {
        console.error("Failed to fetch huddle participants:", error);
      }
    },

    async inviteToHuddle({ state }, { userIds }) {
      try {
        if (!state.activeHuddle?.session_id) return;
        return await HuddleService.inviteToHuddle(
          state.activeHuddle.session_id,
          userIds
        );
      } catch (error) {
        console.error("Failed to invite to huddle:", error);
        throw error;
      }
    },

    setIncomingInvite({ commit }, invite) {
      commit("SET_INCOMING_INVITE", invite);
    },

    clearIncomingInvite({ commit }) {
      stopCallingSound();
      commit("SET_INCOMING_INVITE", null);
    },

    setActiveSpeakers({ commit }, speakerIds) {
      commit("SET_ACTIVE_SPEAKERS", speakerIds);
    },

    syncRemoteParticipants({ commit, rootGetters }, remoteParticipants) {
      const localUserId = rootGetters["auth/userData"]?.id;
      commit("SYNC_REMOTE_PARTICIPANTS", { remoteParticipants, localUserId });
    },

    handleHuddleStarted({ commit, state }, event) {
      const huddle = {
        session_id: event.session_id,
        group_id: event.group_id,
        created_by: event.created_by,
        status: "active",
        started_at: event.started_at,
        participant_count: event.participant_count || 1,
        ...(event.livekit_ws_url && { livekit_ws_url: event.livekit_ws_url }),
        ...(event.livekit_room_name && { livekit_room_name: event.livekit_room_name }),
      };

      const currentHuddle = state.activeHuddle;
      if (currentHuddle && String(currentHuddle.group_id) === String(event.group_id)) {
        commit("SET_ACTIVE_HUDDLE", { ...currentHuddle, ...huddle });
      } else if (!currentHuddle) {
        commit("SET_ACTIVE_HUDDLE", huddle);
      }
    },

    async handleHuddleEnded({ commit, state }, event) {
      if (
        state.activeHuddle &&
        String(state.activeHuddle.session_id) === String(event.session_id)
      ) {
        commit("SET_CONNECTION_STATE", "disconnected");
        await livekitManager.disconnect();
        commit("CLEAR_HUDDLE");
      }
      if (
        state.incomingInvite &&
        String(state.incomingInvite.session_id) === String(event.session_id)
      ) {
        commit("SET_INCOMING_INVITE", null);
      }
    },

    handleParticipantJoined({ commit, state }, event) {
      if (
        state.activeHuddle &&
        String(state.activeHuddle.session_id) === String(event.session_id)
      ) {
        commit("ADD_PARTICIPANT", {
          user_id: event.user_id,
          joined_at: event.joined_at || new Date().toISOString(),
          muted: false,
          video_enabled: false,
        });
      }
    },

    handleParticipantLeft({ commit, state }, event) {
      if (
        state.activeHuddle &&
        String(state.activeHuddle.session_id) === String(event.session_id)
      ) {
        commit("REMOVE_PARTICIPANT", event.user_id);
      }
    },

    handleParticipantMuted({ commit, state }, event) {
      if (
        state.activeHuddle &&
        String(state.activeHuddle.session_id) === String(event.session_id)
      ) {
        commit("UPDATE_PARTICIPANT_MUTE", {
          userId: event.user_id,
          muted: event.muted,
        });
      }
    },
  },

  getters: {
    activeHuddle: (state) => state.activeHuddle,
    participants: (state) => state.participants,
    participantCount: (state) => state.participants.length || state.activeHuddle?.participant_count || 0,
    activeSpeakerIds: (state) => state.activeSpeakerIds,
    isMuted: (state) => state.isMuted,
    videoEnabled: (state) => state.videoEnabled,
    screenSharing: (state) => state.screenSharing,
    isJoining: (state) => state.isJoining,
    isLeaving: (state) => state.isLeaving,
    connectionState: (state) => state.connectionState,
    isConnected: (state) => state.connectionState === "connected",
    incomingInvite: (state) => state.incomingInvite,
    panelMinimized: (state) => state.panelMinimized,
    memberGroupIds: (state) => state.memberGroupIds,
    error: (state) => state.error,
    isCurrentUserInHuddle: (state) => state.connectionState === "connected",
    huddleGroupId: (state) => state.activeHuddle?.group_id || null,
  },
};
